import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { forwardRef, useCallback, useRef } from 'react';
import { MDXEditor } from '@mdxeditor/editor';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import type { IApiLegalDocumentType } from '~/lib/api/modules/LegalDocuments';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { usePersistentForm } from '~/components/utils/PersistentForm';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { LegalDocumentFormMainSection } from './LegalDocumentFormMainSection';

export type ILegalDocumentFormValues = {
  type?: IApiLegalDocumentType;
  title?: string;
  markdown?: string;
  outdated?: boolean;
  required?: boolean;
};

export type IAppFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<ILegalDocumentFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting'
  > & {
    mode: IFormMode;
    defaultValues?: DefaultValues<ILegalDocumentFormValues>;
    onSubmit?: SubmitHandler<ILegalDocumentFormValues>;
  };

export const LegalDocumentForm = forwardRef<HTMLFormElement, IAppFormProps>(
  function LegalDocumentForm(props, ref) {
    const { sx, mode, defaultValues, onSubmit, ...other } = props;

    const markdownEditorRef = useRef<React.ElementRef<typeof MDXEditor>>(null);
    const onRecoverField = useCallback((fieldName: string, value: unknown) => {
      if (fieldName === 'markdown') {
        markdownEditorRef.current?.setMarkdown(value as string);

        return true;
      }

      return false;
    }, []);

    const form = usePersistentForm<ILegalDocumentFormValues>({
      defaultValues,
      onRecoverField,
    });
    useFormAutoFocus({ form, name: 'type' });

    return (
      <ContentBlock.FormBody
        ref={ref}
        sx={sx}
        form={form}
        onSubmit={onSubmit}
        {...other}
      >
        <LegalDocumentFormMainSection
          form={form}
          mode={mode}
          defaultMarkdown={defaultValues?.markdown}
          markdownEditorRef={markdownEditorRef}
        />
      </ContentBlock.FormBody>
    );
  },
);
